import { useEffect, useState } from "react";
import { X, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCentre } from "@/context/CentreContext";

const FIELD =
  "w-full bg-hp-dark border border-white/10 px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-hp-yellow transition-colors";
const LABEL = "block text-xs font-bold tracking-widest uppercase text-neutral-400 mb-2";

const REQUEST_LABELS = {
  rappel: "Être rappelé",
  devis: "Demander un devis",
  rdv: "Prendre rendez-vous",
  configurateur: "Je veux cette configuration",
};

export default function LeadDialog({ open, onOpenChange, prefill = {} }) {
  const { centres, current } = useCentre();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        request_type: "devis",
        first_name: "", last_name: "", phone: "", email: "",
        plate: "", brand: "", model: "", engine: "", service: "", stage: "",
        centre_slug: current?.slug || "", message: "", vehicle_summary: "",
        consent: false,
        ...prefill,
      });
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Merci d'accepter d'être recontacté.");
    if (!form.first_name || !form.phone || !form.email) return toast.error("Nom, téléphone et email sont requis.");
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast.success("Demande envoyée ! Un conseiller Horsepower vous recontacte rapidement.");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4 sm:p-8"
      onClick={() => onOpenChange(false)} data-testid="lead-dialog-overlay">
      <div className="relative w-full max-w-2xl bg-hp-dark border border-white/10 my-4"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-black">
          <div>
            <p className="label-eyebrow">Horsepower Garage</p>
            <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-1">
              {REQUEST_LABELS[form.request_type] || "Demander un devis"}
            </h3>
          </div>
          <button onClick={() => onOpenChange(false)} className="text-neutral-400 hover:text-hp-yellow transition-colors"
            data-testid="lead-dialog-close"><X size={24} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="lead-form">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(REQUEST_LABELS).map(([k, v]) => (
              <button type="button" key={k} onClick={() => set("request_type", k)}
                data-testid={`lead-type-${k}`}
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${
                  form.request_type === k ? "bg-hp-yellow text-black border-hp-yellow" : "border-white/15 text-neutral-300 hover:border-hp-yellow"
                }`}>{v}</button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={LABEL}>Prénom *</label>
              <input className={FIELD} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="lead-first-name" /></div>
            <div><label className={LABEL}>Nom</label>
              <input className={FIELD} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="lead-last-name" /></div>
            <div><label className={LABEL}>Téléphone *</label>
              <input className={FIELD} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone" /></div>
            <div><label className={LABEL}>Email *</label>
              <input type="email" className={FIELD} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" /></div>
            <div><label className={LABEL}>Immatriculation</label>
              <input className={FIELD} value={form.plate} onChange={(e) => set("plate", e.target.value)} data-testid="lead-plate" /></div>
            <div><label className={LABEL}>Marque</label>
              <input className={FIELD} value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="lead-brand" /></div>
            <div><label className={LABEL}>Modèle</label>
              <input className={FIELD} value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="lead-model" /></div>
            <div><label className={LABEL}>Motorisation</label>
              <input className={FIELD} value={form.engine} onChange={(e) => set("engine", e.target.value)} data-testid="lead-engine" /></div>
            <div><label className={LABEL}>Prestation souhaitée</label>
              <select className={FIELD} value={form.service} onChange={(e) => set("service", e.target.value)} data-testid="lead-service">
                <option value="">— Choisir —</option>
                {["Demande de gain", "Stage 1", "Stage 2", "Stage 3", "Conversion E85", "Banc de puissance", "Diagnostic", "Préparation automobile", "Entretien"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className={LABEL}>Centre souhaité <span className="text-hp-yellow">*</span></label>
              <select className={FIELD} value={form.centre_slug} onChange={(e) => set("centre_slug", e.target.value)} required data-testid="lead-centre">
                <option value="">— Choisir —</option>
                {centres.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select></div>
          </div>

          <div><label className={LABEL}>Message</label>
            <textarea rows={3} className={FIELD} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="lead-message" /></div>

          <label className="flex items-start gap-3 cursor-pointer text-sm text-neutral-300">
            <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
              className="mt-0.5 h-5 w-5 accent-[#FFED00]" data-testid="lead-consent" />
            <span>J'accepte d'être recontacté par Horsepower Garage. *</span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3">
            <button type="submit" disabled={submitting} className="hp-cta flex-1 px-6 py-4 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="lead-submit">
              <Check size={18} /> {submitting ? "Envoi..." : "Envoyer ma demande"}
            </button>
            {current?.phone && (
              <a href={`tel:${current.phone.replace(/\s/g, "")}`} className="hp-ghost px-6 py-4 flex items-center justify-center gap-2" data-testid="lead-call">
                <Phone size={18} /> {current.phone}
              </a>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
